import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
   IconAudioMute,
   IconDeclineCallIcon,
   IconMic,
   IconVideoMute,
   IconVideo,
   IconNewWindow,
   IconWhiteClose,
} from '@/assets/images';
import styles from './ControlsBar.module.scss';
import { useLanguage } from '@/localization/useLanguage';
import { getLocalUserName } from '../../../utils/helper';
import { isEnabled } from '@/features/calls/subFeatureRegistry';
import { showInfoToast } from '@/shared/utils';
import { VITE_MAX_SCREEN_SHARES } from '@/processENV';
import { startScreen, stopScreen } from '@/features/calls/services/callService';

export default function ControlsBar({
   isAudioMuted,
   setAudioMuted,
   videoMuted,
   setvideoMuted,
   onEndCall,
   isReconnecting,
}) {
   const { t } = useLanguage();
   const ongoingCallParticipants = useSelector(state => state.callkitData?.ongoingCallParticipants || []);
   const currentUserId = getLocalUserName();
   const isLocalSharing = ongoingCallParticipants.some(p => p.userId === currentUserId + '-screen');
   const activeScreenSharesCount = ongoingCallParticipants.filter(p => p.userId?.endsWith('-screen')).length;

   // Button should only be disabled if:
   // 1. Reconnecting OR
   // 2. Someone else is sharing AND the limit is reached AND user is not current sharing.
   const isScreenShareDisabled = !isLocalSharing && activeScreenSharesCount >= VITE_MAX_SCREEN_SHARES;

   const screenShareEnabled = isEnabled('screenShare');

   const lastScreenToggleTime = React.useRef(0);
   const handleScreenShareToggle = () => {
      const now = Date.now();
      if (now - lastScreenToggleTime.current < 2000) return;
      lastScreenToggleTime.current = now;

      if (isReconnecting) return;
      if (isScreenShareDisabled) {
         showInfoToast(t('calls.MAXIMUM_SCREEN_SHARE'));
         return;
      }
      if (isLocalSharing) {
         stopScreen(() => {});
      } else {
         startScreen(() => {});
      }
   };

   return (
      <div className={`ct-flex ct-aiCenter ct-jcCenter ${styles.controlBar}`}>
         <div className={`ct-flex ct-gap16  ${styles.controlButtonWrapper}`}>
            <button
               className={`ct-flex ct-aiCenter ct-jcCenter  ${[styles.controlButton, isAudioMuted ? styles.danger : ''].join(' ')} ${isReconnecting ? styles.disabled : ''}`}
               onClick={() => !isReconnecting && setAudioMuted(!isAudioMuted)}
               disabled={isReconnecting}
            >
               {isAudioMuted ? (
                  <i className="ct-icon sm">
                     <IconAudioMute />
                  </i>
               ) : (
                  <i className="ct-icon sm">
                     <IconMic />
                  </i>
               )}
            </button>
            <button
               className={`ct-flex ct-aiCenter ct-jcCenter  ${[styles.controlButton, videoMuted ? styles.danger : ''].join(' ')} ${isReconnecting ? styles.disabled : ''}`}
               onClick={() => !isReconnecting && setvideoMuted(!videoMuted)}
               disabled={isReconnecting}
            >
               {videoMuted ? (
                  <i className="ct-icon sm">
                     <IconVideoMute />
                  </i>
               ) : (
                  <i className="ct-icon sm">
                     <IconVideo />
                  </i>
               )}
            </button>

            {screenShareEnabled && (
               <button
                  className={`ct-flex ct-aiCenter ct-jcCenter  ${[styles.controlButton, isLocalSharing ? styles.danger : ''].join(' ')} ${isScreenShareDisabled ? styles.disabled : ''}`}
                  onClick={handleScreenShareToggle}
                  title={isLocalSharing ? t('calls.STOP_SHARING') : t('calls.SCREEN_SHARE')}
               >
                  {isLocalSharing ? (
                     <i className="ct-icon xs">
                        <IconWhiteClose />
                     </i>
                  ) : (
                     <i className="ct-icon sm">
                        <IconNewWindow />
                     </i>
                  )}
               </button>
            )}

            <button
               className={`ct-flex ct-aiCenter ct-jcCenter  ${[styles.controlButton, styles.leave].join(' ')}`}
               onClick={onEndCall}
               title={t('common.LEAVE_CALL')}
            >
               <i className="ct-icon sm">
                  <IconDeclineCallIcon />
               </i>
            </button>
         </div>
      </div>
   );
}

ControlsBar.propTypes = {
   isAudioMuted: PropTypes.bool,
   videoMuted: PropTypes.bool,
   setAudioMuted: PropTypes.func,
   setvideoMuted: PropTypes.func,
   onEndCall: PropTypes.func,
   isReconnecting: PropTypes.bool,
};
